import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    deleteIncomeUnitById,
    deleteMultipleIncomeUnitsByIds,
    getUnitData,
    resetDeleteIncomeUnitById,
    rollDownIncomeUnits,
    resetRollDownIncomeUnits,
    resetUnitData,
    upsertBatchIncomeUnits
} from '../../../redux/actions/incomeUnit/incomeUnit';
import { resetSaving, setSaveResultSuccess } from '../../../redux/actions/saveData/saveData';
import { GetUnitsState } from '../../../redux/reducers/getIncomeUnit/getUnitsReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import {
    selectDeleteIncomeUnit,
    selectGetUnits,
    selectProfileGetOverview,
    selectRollDownUnits,
    selectSaveData,
    selectSaveResult
} from '../../../redux/selectors/exportSelectorPaths';
import { BaseAsyncReducerState } from '../../../redux/types';
import { defaultNumberPerPage } from '../../../assets/data/pagination/pagination';
import IncomeUnitType, { getDefaultUnit } from '../../../types/unit/IncomeUnit';
import { ApplyColumnInfo } from '../../../utils/ApplyToAll/ApplyToAll';
import {
    exactMatcher,
    FormError,
    getFormErrorByName,
    useFormErrorContext
} from '../../../utils/context/FormErrorContext';
import { useNavigation } from '../../../utils/context/NavigationContext';
import { ColumnType, FilterColumnInfo, SortingColumnInfo } from '../../../utils/DataTable/ColumnInfo';
import { Filter, filterData } from '../../../utils/DataTable/GenericFilter/GenericFilter';
import GenericSortingTableHeader from '../../../utils/DataTable/GenericSorting/GenericSortingTableHeader';
import { useReadOnly } from '../../../utils/ReadOnlyContext';
import { sortData, SortRule } from '../../../utils/sorting';
import Asterisk from '../Asterisk/Asterisk';
import DataTable from '../DataTable/DataTable';
import { Option } from '../Dropdown/Dropdown';
import BasicUnitRenderer from './BasicUnitRenderer';
import { UnitTableControls } from './UnitTableControls';
import './UnitTable.css'
import { setNextActiveMenuItem, setPrevActiveMenuItem } from '../../../redux/actions/menu/menu';
import { initialMenuItem } from '../../../redux/reducers/getMenu/getMenuReducer';
import { downloadCSV, getCSVCellValue } from '../../../utils/csvExport';

type UnitColumnTranslation = {
    columnNameTranslation: string
    columnTooltipTranslation: string
}

export type UnitColumn = {
    columnName: string
    required?: boolean
    type?: ColumnType
    dropdown?: Option[] | Record<string, Option[]>
    dropdownSourceColumn?: string
    translationSuffix?: string
    headerClassName?: string
    castedTo?: ColumnType
    isValid?: (unit: IncomeUnitType) => boolean
    onPostUnitFieldChange?: (unit: IncomeUnitType) => IncomeUnitType
    maxDate?: Date
    tooltipForValue?: boolean
}

interface UnitTableProps {
    units: IncomeUnitType[]
    setUnits: React.Dispatch<React.SetStateAction<IncomeUnitType[]>>
    unitType: "1" | "2" | "3" | "4" | "5" | "6",
    translationScope: "lease_info" | "suite_breakdown"
    isInternal?: boolean
    deleteColumn?: boolean
    columns: UnitColumn[]
    applyToAllColumns: UnitColumn[]
    triggerDeleteAllUnits?: boolean
    setTriggerDeleteAllUnits?: React.Dispatch<React.SetStateAction<boolean>>
    triggerRollDownAllUnits?: boolean
    setTriggerRollDownAllUnits?: React.Dispatch<React.SetStateAction<boolean>>
    guidedWizardButton?: JSX.Element | undefined
    setIsErrorVisible?: React.Dispatch<React.SetStateAction<(error: FormError) => boolean>>
    setCustomErrorDisplay?: React.Dispatch<React.SetStateAction<string | JSX.Element | undefined>>,
    shouldRefreshUnits?: boolean
    setShouldRefreshUnits?: React.Dispatch<React.SetStateAction<boolean>>
    accordion?: boolean,
    showAffordableHousingTable?: boolean,
    affordableHousingUnitCount?: number | null,
    propClass: string
}

export default function UnitTable({
    units,
    setUnits,
    unitType,
    translationScope,
    isInternal = false,
    deleteColumn = false,
    columns,
    applyToAllColumns = [],
    triggerDeleteAllUnits,
    setTriggerDeleteAllUnits,
    triggerRollDownAllUnits,
    setTriggerRollDownAllUnits,
    guidedWizardButton = <></>,
    setIsErrorVisible,
    setCustomErrorDisplay,
    shouldRefreshUnits,
    setShouldRefreshUnits,
    showAffordableHousingTable,
    affordableHousingUnitCount,
    propClass
}: UnitTableProps) {
    const dispatch = useDispatch()
    const { errors, clearErrors, updateErrors, removeError } = useFormErrorContext()
    const { direction } = useNavigation()
    const { isReadOnly } = useReadOnly()
    const { t } = useTranslation(["unit_table", "common"])

    // Redux State
    const deleteIncomeUnitState: BaseAsyncReducerState = useSelector(selectDeleteIncomeUnit)
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const getUnitsState: GetUnitsState = useSelector(selectGetUnits)
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const rollDownUnitState: BaseAsyncReducerState = useSelector(selectRollDownUnits)

    // React State
    const [displayedUnits, setDisplayedUnits] = useState<IncomeUnitType[]>([])
    const [refreshDisplayedUnits, setRefreshDisplayedUnits] = useState<boolean>(false)
    const [filters, setFilters] = useState<Filter<IncomeUnitType>[]>([])
    const [sortRules, setSortRules] = useState<SortRule<IncomeUnitType>[]>([])
    const [currentPage, setCurrentPage] = useState(1)
    const [numberPerPage, setNumberPerPage] = useState(defaultNumberPerPage)
    const [unitsFetched, setUnitsFetched] = useState(false)
    const [fetchingUnits, setFetchingUnits] = useState(false)
    const [refreshErrors, setRefreshErrors] = useState<boolean>(false)

    const campaignStatusCode = sessionStorage.getItem('campaignStatusCode')
    const currentSnapshotId = sessionStorage.getItem('currentSnapshotId') as string
    const propertyCode = sessionStorage.getItem("propertyCode") as string
    const rollNumber = sessionStorage.getItem("rollNumber") as string
    const selectedYear = sessionStorage.getItem("activeCampaignYear") as string
    const profileTypeCode = profileOverview.profile.profile_type_code
    const isExternalInProgress = campaignStatusCode === "EXTERNAL_IN_PROGRESS"
    const showTableDataControls = isInternal && isExternalInProgress && !isReadOnly
    const translationKey = translationScope + "_" + (isInternal ? "internal" : "external") + "."

    const generateSortingColumns = (): SortingColumnInfo[] => {
        const sortingColumns = columns.map((column) => {
            const translation = getColumnTranslation(column)
            let display: string | JSX.Element = translation.columnNameTranslation
            if (column.required) {
                display = (
                    <div className="flex flex-row gap-025em">
                        <span>{display}</span>
                        <Asterisk />
                    </div>
                )
            }
            const result: SortingColumnInfo = {
                "id": column.columnName,
                "type": column.type,
                "display": display,
                "tooltip": translation.columnTooltipTranslation,
                "headerClassName": column.headerClassName
            }

            if (column.dropdown) {
                // If dropdownsourceColumn is defined then we flatten the map for lookups
                const optionArr: Option[] = column.dropdownSourceColumn ? Object.values(column.dropdown as Record<string, Option[]>).flat() : column.dropdown as Option[]
                return {
                    ...result,
                    "dataLkpFunc": (val: unknown) => optionArr.find((option) => String(option.value) === String(val))?.text
                }
            }

            return result
        })
        if (deleteColumn) {
            return [...sortingColumns, { sortingDisabled: true }]
        } else {
            return sortingColumns
        }
    }

    const generateFilterColumns = (): FilterColumnInfo[] => {
        return columns.map((column) => {
            const translation = getColumnTranslation(column)
            const result: FilterColumnInfo = {
                "id": column.columnName,
                "display": translation.columnNameTranslation,
                "type": column.type
            }

            if (column.dropdown && column.columnName !== "analysisFlag") {
                // If dropdownsourceColumn is defined then we flatten the map for lookups
                const optionArr: Option[] = column.dropdownSourceColumn ?
                    Object.values(column.dropdown as Record<string, Option[]>).flat()
                    :
                    column.dropdown as Option[]
                return {
                    ...result,
                    "dataLkpFunc": (val: unknown) => optionArr.find((option) => String(option.value) === String(val))?.text
                }
            }

            return result
        })
    }

    const generateApplyToAllColumns = (): ApplyColumnInfo[] => {
        return applyToAllColumns.map((column) => {
            const translation = getColumnTranslation(column)
            const result: ApplyColumnInfo = {
                "key": column.columnName,
                "display": translation.columnNameTranslation,
                "type": column.type
            }

            if (column.dropdown) {
                // If dropdownsourceColumn is defined then we flatten the map for lookups
                const optionArr: Option[] = column.dropdownSourceColumn ? Object.values(column.dropdown as Record<string, Option[]>).flat() : column.dropdown as Option[]

                return {
                    ...result,
                    "dropdown": optionArr
                }
            }

            return result
        })
    }

    const exportAffordableUnitsAsCSV = () => {
        downloadCSV(
            `${rollNumber}_${selectedYear}_ARH_table.csv`,
            columns.map((column) => getColumnTranslation(column).columnNameTranslation),
            displayedUnits.map((unit) => columns.map((column) => getCSVCellValue(unit, column)))
        )
    }

    const generateComponentId = () => {
        // Temporary local fallback for browsers/environments without crypto.randomUUID.
        const random = typeof globalThis.crypto?.randomUUID === "function"
            ? String(globalThis.crypto.randomUUID())
            : `${Date.now()}.${Math.random().toString(36).slice(2)}`
        const parts = random.split(".")
        const componentId = parts[parts.length - 1]
        return componentId
    }

    // Used to identify which unpersisted units have spectral errors
    const addComponentIdToUnits = useCallback((units: IncomeUnitType[]): IncomeUnitType[] => {
        return units.map(unit => {
            if (unit.componentId) {
                return unit
            }

            let componentId = null
            if (unit.tenantId) {
                componentId = String(unit.tenantId)
            } else {
                componentId = generateComponentId()
            }
            unit["componentId"] = componentId
            return unit
        })
    }, [])

    const getColumnTranslation = useCallback((data: UnitColumn): UnitColumnTranslation => {
        const affordableHousingOverrideColumns = ["netLeasableArea", "monthlyMarketRent", "furnishedFlag"]
        const baseColumnKey = `unit-table.${translationKey}${data.columnName}`
        const baseTooltipKey = `unit-table.${translationKey}tooltips.${data.columnName}`

        if (showAffordableHousingTable && affordableHousingOverrideColumns.includes(data.columnName)) {
            return {
                "columnNameTranslation": t(`${baseColumnKey}2`),
                "columnTooltipTranslation": t(`${baseTooltipKey}2`,
                    { defaultValue: false }
                )
            }
        }
        else {
            return {
                "columnNameTranslation": t(`${baseColumnKey}`),
                "columnTooltipTranslation": t(`${baseTooltipKey}`,
                    { defaultValue: false }
                )
            }
        }
    }, [t, translationKey, showAffordableHousingTable])

    const isNewUnit = (unit: IncomeUnitType): boolean => {
        return unit.tenantId === null || unit.tenantId === undefined
    }

    const getNewUnits = useCallback(() => {
        return units.filter(unit => isNewUnit(unit))
    }, [units])

    const getExistingUnits = useCallback(() => {
        return units.filter(unit => !isNewUnit(unit))
    }, [units])

    const createUnit = () => {
        const unit = {
            ...getDefaultUnit(),
            snapshotId: Number(currentSnapshotId),
            componentId: generateComponentId(),
            unitTypeCode: unitType
        } as IncomeUnitType
        setUnits([unit, ...units])
        setDisplayedUnits([unit, ...displayedUnits])
    }

    const deleteViewUnit = useCallback((componentId: string | string[]) => {
        if (Array.isArray(componentId)) {
            setUnits(units.filter(u => !componentId.includes(u.componentId as string)))
            setDisplayedUnits(displayedUnits.filter(u => !componentId.includes(u.componentId as string)))
            updateErrors(errors.filter((err) => !componentId.includes(err.primaryKey.split(".")[0])))
        } else {
            setUnits(units.filter(u => u.componentId !== componentId))
            setDisplayedUnits(displayedUnits.filter(u => u.componentId !== componentId))
            updateErrors(errors.filter((err) => !err.primaryKey.startsWith(componentId + ".")))
        }
    }, [displayedUnits, errors, setUnits, units, updateErrors])

    const deleteUnit = (unit: IncomeUnitType) => {
        if (isNewUnit(unit)) {
            deleteViewUnit(unit.componentId as string)
        } else {
            dispatch(resetDeleteIncomeUnitById())
            dispatch(deleteIncomeUnitById(currentSnapshotId, String(unit.tenantId)))
        }
    }

    const deleteAllDisplayedUnits = () => {
        const unitsToDelete = displayedUnits.filter(unit => unit.tenantId).map((unit) => unit.tenantId as number)
        if (unitsToDelete.length > 0) {
            dispatch(resetDeleteIncomeUnitById())
            dispatch(deleteMultipleIncomeUnitsByIds(currentSnapshotId, unitsToDelete))
        }
        const newUnitsToDelete = displayedUnits.filter(unit => !unit.tenantId).map((unit) => unit.componentId as string)
        if (newUnitsToDelete.length > 0) {
            deleteViewUnit(newUnitsToDelete)
        }
    }

    const deleteAllUnits = useCallback(() => {
        const unitsToDelete = units.filter(unit => unit.tenantId).map((unit) => unit.tenantId as number)
        if (unitsToDelete.length > 0) {
            dispatch(resetDeleteIncomeUnitById())
            dispatch(deleteMultipleIncomeUnitsByIds(currentSnapshotId, unitsToDelete))
        }
        const newUnitsToDelete = units.filter(unit => !unit.tenantId).map((unit) => unit.componentId as string)
        if (newUnitsToDelete.length > 0) {
            deleteViewUnit(newUnitsToDelete)
        }
    }, [currentSnapshotId, deleteViewUnit, dispatch, units])

    const rollDownAllUnits = useCallback(() => {
        dispatch(resetSaving())
        clearErrors()
        const unitsToBeDeleted = units.filter(unit => unit.tenantId)
        const unitsToDelete = unitsToBeDeleted.map((unit) => unit.tenantId as number)
        if (unitsToDelete.length > 0) {
            const unitsToAdd: IncomeUnitType[] = []
            unitsToBeDeleted.forEach((unit) => {
                for (let i = 0; i < (unit.unitCount ?? 1); i++) {
                    unitsToAdd.push({
                        ...getDefaultUnit(),
                        snapshotId: Number(currentSnapshotId),
                        componentId: generateComponentId(),
                        unitTypeCode: unitType,
                        styleTypeCode: unit.styleTypeCode,
                        unitSubtypeCode: unit.unitSubtypeCode,
                        unitCount: 1
                    } as IncomeUnitType)
                }
            })
            dispatch(rollDownIncomeUnits(currentSnapshotId, {
                "delete": unitsToDelete,
                "added": unitsToAdd,
                "property_code": propertyCode,
            }))
        }
        const newUnitsToDelete = units.filter(unit => !unit.tenantId).map((unit) => unit.componentId as string)
        if (newUnitsToDelete.length > 0) {
            deleteViewUnit(newUnitsToDelete)
        }
    }, [currentSnapshotId, deleteViewUnit, dispatch, units, propertyCode, unitType, clearErrors])

    const replaceViewUnit = (unit: IncomeUnitType) => {
        setUnits(prevData =>
            prevData.map(u =>
                u.componentId === unit.componentId ? unit : u
            )
        )
        setDisplayedUnits(prevData =>
            prevData.map(u =>
                u.componentId === unit.componentId ? unit : u
            )
        )
    }

    const updateValue = (
        unit: IncomeUnitType,
        column: string | UnitColumn,
        value: string | number | boolean | null | undefined
    ) => {
        const columnName = (typeof column === "string") ? column : column.columnName
        removeError(getFormErrorByName(errors, unit.componentId + "." + columnName, exactMatcher)?.primaryKey as string)
        const updatedUnit: IncomeUnitType = {
            ...unit,
            [columnName]: value
        }
        if (typeof column !== "string" && column.onPostUnitFieldChange) {
            column.onPostUnitFieldChange(updatedUnit)
        }
        replaceViewUnit(updatedUnit)
    }

    const updateAllValues = (key: keyof IncomeUnitType, value: string | number | boolean | null | undefined) => {
        const column = columns.find((c) => c.columnName === key)
        if (!column) {
            return
        }

        let updatedValue = value === undefined ? null : value
        if (column.castedTo && updatedValue !== null) {
            switch (column.castedTo) {
                case "string":
                    updatedValue = String(updatedValue)
                    break
                case "integer":
                case "number":
                    updatedValue = Number(updatedValue)
                    break
            }
        }
        const user = localStorage.getItem("username") ?? "SYSTEM"
        const today = new Date().toISOString()
        displayedUnits.forEach((unit) => {
            if (key === "analysisFlag") {
                const updatedUnit: IncomeUnitType = {
                    ...unit,
                    "analysisFlag": updatedValue as string,
                    "analysisReviewedBy": user,
                    "analysisReviewedDate": today,
                }
                replaceViewUnit(updatedUnit)
            } else {
                updateValue(unit, column, updatedValue)
            }
        })
    }

    const getPagniatedUnits = useCallback(() => {
        const lastIdx = currentPage * numberPerPage
        const firstIdx = lastIdx - numberPerPage
        return displayedUnits.slice(firstIdx, lastIdx)
    }, [currentPage, displayedUnits, numberPerPage])

    useEffect(() => {
        const getFilteredAndSortedData = () => {
            if (units && units.length > 0) {
                const filteredData = filterData(units, filters)
                const sorted = sortData<IncomeUnitType>(filteredData, sortRules)
                return sorted
            }
            return []
        }

        if (refreshDisplayedUnits) {
            setRefreshDisplayedUnits(false)
            setDisplayedUnits(getFilteredAndSortedData())
        }
    }, [filters, refreshDisplayedUnits, sortRules, units])

    useEffect(() => {
        // To capture edge case where all units are removed but table is not refreshed correctly
        if (units && (units.length > 0 || (units.length === 0 && displayedUnits.length > 0))) {
            setRefreshDisplayedUnits(true)
        }
        // displayedUnits has to be excluded because it's causing an infinite loop to refresh the table
        // eslint-disable-next-line
    }, [filters, sortRules, units])

    // Error checking
    useEffect(() => {
        if (refreshErrors) {
            setRefreshErrors(false)

            const isErrorVisible = (error: FormError) => {
                const errorComponentId = error.primaryKey.split(".")[0]
                if (errorComponentId === "additionalInfo") {
                    return true
                }

                const pagniatedUnits = getPagniatedUnits()
                const pagniatedComponentIds = pagniatedUnits.map(u => u.componentId as string)
                if (error && error.primaryKey) {
                    return pagniatedComponentIds.includes(errorComponentId)
                }
                return true
            }
            if (setIsErrorVisible) {
                setIsErrorVisible(() => isErrorVisible)
            }

            if (errors.length > 0 && setCustomErrorDisplay) {
                let customErrorDisplay = "Total error count: " + errors.length
                if (units.length > displayedUnits.length) {
                    const displayedUnitsIds = displayedUnits.map(unit => unit.componentId as string)
                    const displayedUnitsErrors = errors.filter((error) => displayedUnitsIds.includes(error.primaryKey.split(".")[0]))
                    const displayedUnitsTotalErrorDisplay = "after apply filters: " + displayedUnitsErrors.length
                    customErrorDisplay = customErrorDisplay + ", " + displayedUnitsTotalErrorDisplay
                }
                setCustomErrorDisplay(customErrorDisplay)
            }
        }
    }, [displayedUnits, errors, getPagniatedUnits, refreshErrors, setCustomErrorDisplay, setIsErrorVisible, units])

    useEffect(() => {
        if (errors.length > 0) {
            setRefreshErrors(true)
        }
    }, [currentPage, displayedUnits, errors, numberPerPage])

    // Initial data fetch request
    useEffect(() => {
        if (!fetchingUnits && !unitsFetched) {
            dispatch(resetUnitData())
            dispatch(getUnitData(currentSnapshotId, unitType))
            setFetchingUnits(true)
        }
    }, [currentSnapshotId, dispatch, fetchingUnits, unitType, unitsFetched])

    // Process unit response from Income Service
    useEffect(() => {
        if (fetchingUnits && !unitsFetched && !getUnitsState.loading) {
            setFetchingUnits(false)
            if (getUnitsState.success) {
                // Add componentId before setting array
                const units = addComponentIdToUnits(getUnitsState.units)
                setUnits(units)
                setFilters([])
                setSortRules([])
                setUnitsFetched(true)
            }
        }
    }, [addComponentIdToUnits, fetchingUnits, getUnitsState.loading, getUnitsState.success, getUnitsState.units, setUnits, unitsFetched])

    // On page save logic
    useEffect(() => {
        if (saveData.saving) {
            dispatch(resetSaving())
            clearErrors()

            if (isReadOnly) {
                dispatch(setSaveResultSuccess())
                return
            }

            const triggerSpectral = isInternal || direction !== "backward"
            dispatch(upsertBatchIncomeUnits(currentSnapshotId, {
                "added": getNewUnits(),
                "updated": getExistingUnits(),
                "property_code": propertyCode,
                "profile_type_code": profileTypeCode,
                "trigger_spectral": triggerSpectral,
                "show_affordable_housing_table": showAffordableHousingTable === true,
                "affordable_housing_unit_count": affordableHousingUnitCount,
                "is_internal": isInternal
            }))
        }
    }, [clearErrors, currentSnapshotId, direction, dispatch, getExistingUnits, getNewUnits, isInternal, isReadOnly, profileTypeCode, propertyCode, saveData.saving, units, showAffordableHousingTable, affordableHousingUnitCount])

    // Process unit save/update response from Income Service 
    useEffect(() => {
        if (saveResultState.loadingCount >= 0 && saveResultState.status) {
            if (saveResultState.status === 200) {
                if ("data" in saveResultState && saveResultState.data && Array.isArray(saveResultState.data)) {
                    console.log("UnitTable effect saveResult data isArray")
                    setUnitsFetched(false)
                }
            }
            else { // Hotfix: PIER2-1825 - pause next/back navigation when spectral errors are returned (this handles the upsert income units response)
                dispatch(setNextActiveMenuItem(initialMenuItem))
                dispatch(setPrevActiveMenuItem(initialMenuItem))
            }
        }
    }, [saveResultState, dispatch])

    useEffect(() => {
        if (!rollDownUnitState.loading && rollDownUnitState.success) {
            dispatch(resetRollDownIncomeUnits());
            setUnitsFetched(false)
        }
    }, [dispatch, rollDownUnitState])

    // Process deleted units
    useEffect(() => {
        if (deleteIncomeUnitState.success) {
            const deletedData = deleteIncomeUnitState.data
            if (Array.isArray(deletedData)) {
                const deletedTenantIds = deletedData as unknown as number[]
                setUnits(units.filter(unit => !deletedTenantIds.includes(unit.tenantId as number)))
                setFilters([])
                setSortRules([])
                clearErrors()
            } else {
                deleteViewUnit(String(deletedData))
            }
            dispatch(resetDeleteIncomeUnitById())
        }
    }, [clearErrors, deleteIncomeUnitState, deleteViewUnit, dispatch, setUnits, units])

    // Handle unit refresh signal from parent
    useEffect(() => {
        if (shouldRefreshUnits && setShouldRefreshUnits) {
            setUnitsFetched(false)
            setShouldRefreshUnits(false)
        }
    }, [shouldRefreshUnits, setShouldRefreshUnits])

    // Parent signal to delete all units
    useEffect(() => {
        if (triggerDeleteAllUnits) {
            console.log("UnitTable got triggerDeleteAllUnits")
            deleteAllUnits()
            if (setTriggerDeleteAllUnits) {
                setTriggerDeleteAllUnits(false)
            }
        }
    }, [deleteAllUnits, setTriggerDeleteAllUnits, triggerDeleteAllUnits])

    // Parent signal to reset all units
    useEffect(() => {
        if (triggerRollDownAllUnits) {
            console.log("UnitTable got triggerRollDownAllUnits")
            rollDownAllUnits()
            if (setTriggerRollDownAllUnits) {
                setTriggerRollDownAllUnits(false)
            }
        }
    }, [rollDownAllUnits, setTriggerRollDownAllUnits, triggerRollDownAllUnits])

    return (
        <div className={'padding-right-10 padding-top-10' + (propClass ? (" " + propClass) : "")}>
            <UnitTableControls
                onCreateUnit={createUnit}
                onDeleteAllUnits={deleteAllDisplayedUnits}
                onClearMarketRent={() => updateAllValues("monthlyMarketRent", null)}
                filterColumns={generateFilterColumns()}
                applyToAllColumns={generateApplyToAllColumns()}
                handleApplyToAll={updateAllValues}
                filters={filters}
                setFilters={setFilters}
                isInternal={isInternal}
                showTableDataControls={showTableDataControls}
                guidedWizardButton={guidedWizardButton}
                showExportCSVOption={showAffordableHousingTable}
                handleExportCSV={exportAffordableUnitsAsCSV} />

            <DataTable thead={<GenericSortingTableHeader columns={generateSortingColumns()}
                sortRules={sortRules} setSortRules={setSortRules} />}
                className={`suite-breakdown-table ${isInternal ? "internal-table" : 'external-table'}`}
                totalPages={Math.ceil(displayedUnits.length / numberPerPage)}
                currentPage={currentPage}
                changePage={setCurrentPage}
                numberPerPage={numberPerPage}
                changeNumberPerPage={setNumberPerPage}>
                <BasicUnitRenderer
                    columns={columns}
                    units={getPagniatedUnits()}
                    onUnitDelete={deleteUnit}
                    onUnitChange={updateValue} />
            </DataTable>
        </div>
    )
}
